import { DisplayTime } from '@tock/time';
import { IconPeopleFamily } from '@tock/icons';
import { PartyForCard } from 'components/service/party/partyRedesignCard/partyType';
import { Theme, css } from '@emotion/react';
import { Typography } from 'bento';
import {
  isTimeRange,
  usePartyHeader,
} from 'components/service/party/partyRedesignCard/usePartyHeader';
import { partyCardHeaderCssDefs } from 'components/service/party/partyRedesignCard/partyCardCssDefs';

interface Props {
  party: PartyForCard;
}

const PartyRedesignHeaderCompact: React.FC<Props> = ({ party }) => {
  const { name, isLargeParty, expectedPartySize, time } = usePartyHeader(party);

  const renderName = (
    <Typography
      variant="bodyStrong"
      color="default"
      css={css`
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      `}
    >
      {name}
    </Typography>
  );

  let firstLineContent = null;
  let trailingContent = null;

  if (time && !isTimeRange(time)) {
    firstLineContent = (
      <Typography
        variant="caption"
        color="subtle"
        css={css`
          min-width: 58px;
        `}
      >
        <DisplayTime timeValue={time} />
      </Typography>
    );
    trailingContent = renderName;
  } else {
    firstLineContent = renderName;
  }

  return (
    <div
      css={({ colors }: Theme) => css`
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        height: 100%;
        padding-right: 12px;
        border-right: 1px solid ${colors.borderDefault};
      `}
    >
      <div css={partyCardHeaderCssDefs.header({ hasTime: time !== undefined })}>
        {firstLineContent}
      </div>
      {trailingContent}
      <div
        // This affects the styling of the div -- in a manner that is completely within
        // emotion styles -- by using nested styles underneath &:hover variants of parent
        // component styles. emotion doesn't offer a great built-in solution for this
        // but we can apply styles to a custom data- attribute easily enough.
        data-party-size-indicator={!isLargeParty}
        css={partyCardHeaderCssDefs.partySizeIndicator({ isLargeParty, isCompactView: true })}
      >
        {isLargeParty && <IconPeopleFamily css={partyCardHeaderCssDefs.icon} />}
        <Typography variant="bodyStrong" color={isLargeParty ? 'warningDark' : 'default'}>
          {expectedPartySize}
        </Typography>
      </div>
    </div>
  );
};

export default PartyRedesignHeaderCompact;
